import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";

interface DeleteUserGeneratedReportResponse {
  id?: string;
  success?: boolean;
  message?: string;
}

/**
 * Hook to permanently delete a user-generated report.
 * Calls: DELETE /wp-json/smassets/v1/reports/user-generated/{reportId}
 */
export const useDeleteUserGeneratedReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useUserContext();
  const { getApiUrl } = useMultiSiteContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const deleteReport = useCallback(
    async (reportId: string): Promise<DeleteUserGeneratedReportResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl(`wp-json/smassets/v1/reports/${reportId}/user-generated`);

        const response = await fetchWithTokenRefresh(
          url,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        );

        if (!response.ok) {
          throw new Error(`Failed to delete report: ${response.status}`);
        }

        const result = await response.json();
        setLoading(false);
        return result as DeleteUserGeneratedReportResponse;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete report";
        setError(errorMessage);
        setLoading(false);
        return null;
      }
    },
    [user?.authToken, user?.refreshToken, getApiUrl, refreshUserToken]
  );

  return { deleteReport, loading, error };
};
